import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Clock, ListChecks, Lock, Video } from 'lucide-react';
import type { CursoAcademy, LeccionCurso } from './academyApi';
import { tituloNumerado, urlEmbedYoutube } from './academyFormato';
import { BotonCertificado } from './certificado/BotonCertificado';
import { EsqueletoAcademia } from './EsqueletoAcademia';
import { EXAMEN_HABILITADO } from './examenHabilitado';
import {
  BarraLeccionesMobile,
  BarraSiguiente,
  ItemLeccion,
  SheetLecciones,
} from './LeccionesMobile';
import type { ProgresoCurso } from './progresoDerivado';
import { scrollAlTope } from './scrollAlTope';
import { useCurso } from './useAcademia';
import { useEsperaExamen } from './useEsperaExamen';

/**
 * Una lección de un curso — el reproductor con el panel del curso al costado
 * (ADR-2026-08-24-la-leccion-vive-en-la-url).
 *
 * La lección está en la URL (`/academia/:slug/:leccionSlug`) y no en estado
 * interno: así el atrás del navegador recorre las lecciones, un link a una
 * lección puntual se puede compartir, y recargar no vuelve al principio.
 *
 * La URL usa el SLUG de la lección y el progreso usa su uuid: el slug es lo que
 * se comparte y se lee, el uuid es la clave estable contra la que se guarda.
 */
export const CursoDetalle = () => {
  const { slug, leccionId } = useParams<{ slug: string; leccionId?: string }>();
  const { cursos, curso, progreso, cargando, marcarVista } = useCurso(slug);

  if (cargando) return <EsqueletoAcademia />;
  if (!curso || !progreso) return <Navigate to="/academia" replace />;

  return (
    <VistaCurso
      curso={curso}
      cursos={cursos}
      progreso={progreso}
      leccionSlug={leccionId}
      marcarVista={marcarVista}
    />
  );
};

function VistaCurso({
  curso,
  cursos,
  progreso,
  leccionSlug,
  marcarVista,
}: {
  curso: CursoAcademy;
  cursos: CursoAcademy[];
  progreso: ProgresoCurso;
  leccionSlug?: string;
  marcarVista: (leccionId: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const raiz = useRef<HTMLDivElement>(null);
  const [listaAbierta, setListaAbierta] = useState(false);

  const leccion = leccionSlug
    ? curso.lecciones.find((l) => l.slug === leccionSlug)
    : undefined;

  // Sin lección en la URL (o con una que no existe), a la que sigue según el
  // progreso. `replace` para que el atrás no rebote acá de nuevo.
  useEffect(() => {
    if (!leccion) {
      const destino =
        curso.lecciones.find((l) => l.id === progreso.siguienteLeccionId) ??
        curso.lecciones[0];
      if (destino) {
        navigate(`/academia/${curso.slug}/${destino.slug}`, { replace: true });
      }
    }
  }, [leccion, curso, progreso.siguienteLeccionId, navigate]);

  // Al cambiar de lección, arriba de todo. Va por `scrollAlTope` y no por
  // `window.scrollTo` porque dentro del shell el que scrollea es el `<main>`.
  useEffect(() => {
    scrollAlTope(raiz.current);
    setListaAbierta(false);
  }, [leccionSlug]);

  /* La lección se marca al ABRIRLA (ADR-2026-08-24-la-leccion-se-marca-al-abrirla):
     el avance sigue a la navegación y el porcentaje sube solo. Es idempotente del
     lado del servidor, así que volver a una lección ya vista no hace nada. */
  useEffect(() => {
    if (leccion) void marcarVista(leccion.id);
  }, [leccion, marcarVista]);

  if (!leccion) return null;

  const indice = curso.lecciones.findIndex((l) => l.id === leccion.id);

  return (
    <div ref={raiz} className="mx-auto max-w-content px-4 pb-32 pt-2 sm:px-8 lg:pb-8">
      <div>
        {/* En mobile NO va: el shell ya pone su propio back flotante arriba de todo
            (el header de `Sidebar`, que es `md:hidden`), y los dos juntos eran dos
            flechas de volver una encima de la otra
            (ADR-2026-08-25-en-mobile-el-back-es-el-del-shell). Aparece desde `md`,
            que es exactamente donde el flotante desaparece: así nunca hay dos ni
            ninguno. */}
        <button
          type="button"
          onClick={() => navigate('/academia')}
          className="mb-4 hidden items-center gap-1.5 text-[13.5px] font-medium text-black/50 transition-colors hover:text-brand-primary md:inline-flex"
        >
          <ArrowLeft className="h-4 w-4" />
          Academia Atlas
        </button>

        {/* En mobile el nombre del curso vive DENTRO de la tarjeta de lecciones y
            en chico (ADR-2026-08-25-en-mobile-el-titulo-del-curso-vive-en-la-tarjeta-de-lecciones):
            acá arriba se comía dos renglones enormes y empujaba el video fuera del
            pliegue. */}
        <h1 className="hidden text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black/90 sm:text-[32px] lg:block">
          {tituloNumerado(curso, cursos)}
        </h1>
      </div>

      {/* Tres hijos y no dos —video, panel, resumen— con las celdas puestas a mano
          en `lg`: es lo que hace que el ORDEN DEL DOM sea el de mobile (video →
          panel → resumen) sin cambiar el de desktop. Metiendo el resumen dentro de
          la columna izquierda, en teléfono el panel caía después de las tres
          secciones del resumen y el progreso quedaba enterrado. El panel ocupa las
          dos filas para que el `sticky` tenga por dónde viajar. */}
      {/* `mt-1` en mobile: ese margen separaba la grilla del `h1`, y sin `h1` —que en
          teléfono se mudó adentro de la tarjeta— quedaba como un hueco muerto entre el
          back flotante del shell y la tarjeta de lecciones. Desde `lg` vuelve el margen
          de siempre, donde el título sí está arriba. */}
      <div className="mt-1 grid items-start gap-x-6 gap-y-8 lg:mt-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 lg:col-start-1 lg:row-start-1">
          {/* En qué lección vas y cuánto llevás, ARRIBA del video: en teléfono es lo
              primero que se mira al abrir la pantalla, y abajo quedaba después de un
              16:9 entero (ADR-2026-08-25-en-mobile-el-curso-se-recorre-sin-el-panel). */}
          <BarraLeccionesMobile
            titulo={tituloNumerado(curso, cursos)}
            indice={indice}
            total={curso.lecciones.length}
            progreso={progreso}
            onAbrirLista={() => setListaAbierta(true)}
          />

          <Reproductor leccion={leccion} />

          <div className="mt-5">
            {/* Debajo del video sólo en desktop: en mobile lo dice la tira de arriba. */}
            <p className="hidden text-[12.5px] font-semibold uppercase tracking-[0.06em] text-brand-primary lg:block">
              Lección {indice + 1} de {curso.lecciones.length}
            </p>
            <h2 className="mt-1.5 text-[21px] font-bold leading-snug tracking-[-0.01em] text-brand-black/90">
              {leccion.titulo}
            </h2>
            <p className="mt-1.5 text-[14.5px] leading-relaxed text-black/55">
              {leccion.descripcion}
            </p>
          </div>
        </div>

        {/* Escondido en teléfono: apilado era una lista larguísima entre el video y
            el resumen. Lo reemplazan la tira de arriba, la hoja de lecciones y la
            barra fija de abajo. */}
        <aside className="hidden lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-4 lg:block">
          <PanelCurso curso={curso} progreso={progreso} leccionActualId={leccion.id} />
        </aside>

        <div className="min-w-0 lg:col-start-1 lg:row-start-2">
          <ResumenLeccion leccion={leccion} />
        </div>
      </div>

      {listaAbierta && (
        <SheetLecciones
          curso={curso}
          progreso={progreso}
          leccionActualId={leccion.id}
          onCerrar={() => setListaAbierta(false)}
        />
      )}

      <BarraSiguiente curso={curso} progreso={progreso} indice={indice} />
    </div>
  );
}

/**
 * El video de la lección.
 *
 * Los videos viven en YouTube y la lección guarda su ID
 * (ADR-2026-08-24-lecciones-en-youtube-por-id). Sin ID muestra la miniatura con
 * el estado de "en preparación" en vez de un iframe vacío, así que una lección
 * sin grabar se puede recorrer igual.
 */
function Reproductor({ leccion }: { leccion: LeccionCurso }) {
  if (!leccion.youtubeId) {
    return (
      <div className="relative aspect-video overflow-hidden rounded-2xl bg-[#02062B]">
        <img
          src={leccion.poster}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 grid place-items-center p-6 text-center">
          <div>
            <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/20">
              <Video className="h-5 w-5" strokeWidth={1.9} />
            </span>
            <p className="text-[15px] font-semibold text-white">Video en preparación</p>
            <p className="mx-auto mt-1 max-w-[42ch] text-[13px] leading-relaxed text-white/55">
              La lección todavía no tiene su grabación cargada. Podés seguir el
              recorrido con el resumen de abajo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-video overflow-hidden rounded-2xl bg-black">
      {/* `key`: sin esto, al pasar a la lección siguiente React reusa el mismo
          iframe y sólo le cambia el `src`, lo que deja el reproductor del video
          anterior en pausa arriba del nuevo. */}
      <iframe
        key={leccion.youtubeId}
        src={urlEmbedYoutube(leccion.youtubeId)}
        title={leccion.titulo}
        className="h-full w-full"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

/**
 * El resumen de la lección — un solo bloque de lectura corrida, sin tarjetas ni
 * líneas (ADR-2026-08-24-el-resumen-es-texto-corrido). Tres párrafos de la MISMA
 * lección en tres marcos se leen como tres notas sueltas.
 */
function ResumenLeccion({ leccion }: { leccion: LeccionCurso }) {
  return (
    <section className="space-y-8">
      {leccion.resumen.map((seccion) => (
        <div key={seccion.titulo}>
          <h4 className="text-[18px] font-semibold leading-snug tracking-[-0.01em] text-brand-black/90">
            {seccion.titulo}
          </h4>
          <p className="mt-2 text-[15px] leading-[1.75] text-black/60">
            {seccion.descripcion}
          </p>
        </div>
      ))}
    </section>
  );
}

/** El panel del curso: progreso, lista de lecciones y (si está encendido) el acceso al examen. */
function PanelCurso({
  curso,
  progreso,
  leccionActualId,
}: {
  curso: CursoAcademy;
  progreso: ProgresoCurso;
  leccionActualId: string;
}) {
  const navigate = useNavigate();
  // Mismo motivo que en la barra de mobile: sobre un curso aprobado el contador no se
  // dibuja, y dejarlo vivo es un re-render por segundo de todo el panel.
  const espera = useEsperaExamen(
    progreso.ultimoIntento,
    progreso.aprobado || !EXAMEN_HABILITADO ? 0 : curso.esperaMinutos,
  );
  const hayRevision = progreso.ultimoIntento !== null;

  return (
    <div className="overflow-hidden rounded-2xl border border-black/[0.06] bg-white">
      {/* El porcentaje va GRANDE: es el dato que el usuario viene a mirar cada vez
          que vuelve al panel (ADR-2026-08-24-el-porcentaje-se-muestra-siempre). */}
      <div className="border-b border-black/[0.06] p-4">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-brand-black/90">
            Tu progreso
          </h3>
          <span
            className={`text-[22px] font-bold leading-none tabular-nums tracking-[-0.02em] ${
              progreso.completo ? 'text-emerald-600' : 'text-brand-primary'
            }`}
          >
            {progreso.porcentaje}
            <span className="text-[14px] font-semibold">%</span>
          </span>
        </div>
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${
              progreso.completo ? 'bg-emerald-500' : 'bg-brand-primary'
            }`}
            style={{ width: `${progreso.porcentaje}%` }}
          />
        </div>
        <p className="mt-2 text-[12.5px] text-black/45">
          {progreso.vistas} de {progreso.total} lecciones completadas
        </p>
      </div>

      <div className="p-2">
        <p className="px-2 pb-1 pt-2 text-[11.5px] font-semibold uppercase tracking-[0.06em] text-black/35">
          Lecciones
        </p>
        <ul>
          {curso.lecciones.map((l, i) => (
            <li key={l.id}>
              <ItemLeccion
                leccion={l}
                numero={i + 1}
                esActual={l.id === leccionActualId}
                /* Por ID y no por índice: desde este panel se puede saltar a
                   cualquier lección, así que el progreso es un conjunto y no un
                   cursor. */
                vista={progreso.vistasIds.includes(l.id)}
                onIr={() => navigate(`/academia/${curso.slug}/${l.slug}`)}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Sin examen (ADR-2026-10-07-la-academia-sin-examen-por-ahora) el pie sólo
          existe para cerrar el recorrido: mientras falten lecciones, lo que queda ya
          lo dicen el porcentaje y la lista de arriba. */}
      {!EXAMEN_HABILITADO ? (
        progreso.completo && (
          <div className="border-t border-black/[0.06] p-4">
            <p className="flex items-center gap-2 text-[14px] font-semibold text-emerald-700">
              <BadgeCheck className="h-4.5 w-4.5 shrink-0" strokeWidth={2} />
              Curso completado
            </p>
          </div>
        )
      ) : (
        <div className="border-t border-black/[0.06] p-4">
          {progreso.aprobado ? (
            <div>
              {/* El puntaje va en la MISMA línea que "Curso aprobado", separado con un
                  punto medio, y no debajo: son el mismo hecho —cómo te fue— y en dos
                  renglones el segundo parecía un dato aparte que empujaba al certificado
                  hacia abajo. `flex-wrap` para que en un panel angosto baje entero en vez
                  de cortar el número. */}
              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[14px] font-semibold text-emerald-700">
                <span className="flex items-center gap-2">
                  <BadgeCheck className="h-4.5 w-4.5 shrink-0" strokeWidth={2} />
                  Curso aprobado
                </span>
                {progreso.mejorPuntaje !== null && (
                  <span className="text-[12.5px] font-medium text-black/45">
                    · Mejor puntaje: {progreso.mejorPuntaje}%
                  </span>
                )}
              </p>
              {/* Aprobado no se repite (ADR-2026-08-24-el-curso-aprobado-no-se-repite):
                  la espera existe para que el examen no se resuelva a fuerza de
                  reintentos, y sobre un curso terminado no protege nada.

                  Lo que ocupa ese lugar es el certificado: es LA acción que queda sobre
                  un curso terminado, y tiene que estar a mano cada vez que se vuelve al
                  curso, no sólo en la pantalla de resultado que ya no se va a ver más
                  (ADR-2026-08-25-el-certificado-se-ofrece-al-aprobar-y-en-el-panel). */}
              <BotonCertificado curso={curso} variante="panel" />
            </div>
          ) : progreso.completo && !espera.listo ? (
            <div className="rounded-xl bg-black/[0.03] p-3 text-center">
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-black/45">
                <Clock className="h-3.5 w-3.5" strokeWidth={2} />
                Podés repetirlo en
                <span className="tabular-nums">{espera.etiqueta}</span>
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-black/40">
                Entre un intento y el siguiente pasan{' '}
                {Math.round(curso.esperaMinutos / 60)} horas. Aprovechá para repasar
                las lecciones.
              </p>
            </div>
          ) : progreso.completo ? (
            <button
              type="button"
              onClick={() => navigate(`/academia/${curso.slug}/examen`)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-[14px] font-semibold text-white transition-colors hover:bg-brand-primary-hover"
            >
              <BadgeCheck className="h-4 w-4" strokeWidth={2.2} />
              Tomar examen
            </button>
          ) : (
            <div className="rounded-xl bg-black/[0.03] p-3 text-center">
              <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-black/45">
                <Lock className="h-3.5 w-3.5" strokeWidth={2} />
                Examen bloqueado
              </p>
              <p className="mt-1 text-[12px] leading-relaxed text-black/40">
                Te {progreso.total - progreso.vistas === 1 ? 'queda' : 'quedan'}{' '}
                {progreso.total - progreso.vistas}{' '}
                {progreso.total - progreso.vistas === 1 ? 'lección' : 'lecciones'} para
                habilitarlo.
              </p>
            </div>
          )}

          {/* "Ver mis respuestas" como BOTÓN gris y no como texto en azul: era un
              link discreto que se perdía, y en el curso aprobado —donde ya no hay
              "Repetir examen"— es lo único que queda ahí abajo. */}
          {hayRevision && (
            <button
              type="button"
              onClick={() => navigate(`/academia/${curso.slug}/revision`)}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-xl bg-black/[0.04] py-2.5 text-[13.5px] font-semibold text-black/60 transition-colors hover:bg-black/[0.07] hover:text-black/75"
            >
              <ListChecks className="h-3.5 w-3.5" strokeWidth={2.2} />
              Ver mis respuestas
            </button>
          )}

          {/* Las reglas del examen sólo mientras el examen sea algo que hacer: sobre un
              curso ya aprobado, "8 preguntas · 70% para aprobar" describe un trámite que
              ya pasó y lo único que hace es separar el certificado del borde. */}
          {!progreso.aprobado && (
            <p className="mt-3 text-center text-[11.5px] leading-relaxed text-black/35">
              {curso.cantidadPreguntas} preguntas · {curso.puntajeMinimo}% para aprobar
            </p>
          )}
        </div>
      )}
    </div>
  );
}
